import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Site, Report, convertTimestamp } from '../../../../types/firebase';
import {
  approximateCoordinatesForPublicViewer,
  getPublicApproximateLocationLabel,
  getReportLocationAddressDisplay,
  getScannerDisplayLabel,
  shouldRedactScannerIdentity,
} from '../../../../utils/reportPrivacyDisplay';

const DARK_TILES = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';
const DEFAULT_CENTER: [number, number] = [30, 0];
const DEFAULT_ZOOM = 2;

/** Only treat explicit skimmer status as red; anything else (No Skimmer, Processing, undefined) = cyan. */
const isSkimmerReport = (r: Report): boolean => {
  const s = String(r.status ?? '').trim();
  return s === 'Skimmer Detected!' || s === 'Skimmer Detected';
};

function getReportCoordinates(report: Report, sites: Site[]): { lat: number; lng: number } | null {
  const r = report as any;
  let lat: number | undefined;
  let lng: number | undefined;
  if (r.location?.latitude != null && r.location?.longitude != null) {
    lat = r.location.latitude;
    lng = r.location.longitude;
  } else if (r.location?.coordinates?.latitude != null && r.location?.coordinates?.longitude != null) {
    lat = r.location.coordinates.latitude;
    lng = r.location.coordinates.longitude;
  } else if (r.readerDetails?.coordinates?.latitude != null && r.readerDetails?.coordinates?.longitude != null) {
    lat = r.readerDetails.coordinates.latitude;
    lng = r.readerDetails.coordinates.longitude;
  } else if (r.readerDetails?.device?.coordinates?.latitude != null && r.readerDetails?.device?.coordinates?.longitude != null) {
    lat = r.readerDetails.device.coordinates.latitude;
    lng = r.readerDetails.device.coordinates.longitude;
  } else if (r.coordinates?.latitude != null && r.coordinates?.longitude != null) {
    lat = r.coordinates.latitude;
    lng = r.coordinates.longitude;
  }
  if (lat != null && lng != null) return { lat, lng };
  const siteId = r.readerDetails?.siteId ?? r.siteId;
  if (siteId) {
    const site = sites.find((s) => s.id === siteId);
    const coords = site?.coordinates as { latitude?: number; longitude?: number } | undefined;
    if (coords?.latitude != null && coords?.longitude != null) return { lat: coords.latitude, lng: coords.longitude };
  }
  return null;
}

function getFeedMapPosition(
  report: Report,
  viewerMasterOwnerId: string | undefined,
  sites: Site[]
): { lat: number; lng: number } | null {
  if (shouldRedactScannerIdentity(report, viewerMasterOwnerId)) {
    return approximateCoordinatesForPublicViewer(report, viewerMasterOwnerId, sites);
  }
  return getReportCoordinates(report, sites);
}

function createDotIcon(className: string, pulse = false) {
  return L.divIcon({
    className: 'map-leaflet-icon-wrap',
    html: `<div class="map-dot ${className}">${pulse ? '<div class="pulse-ring"></div>' : ''}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

const cyanIcon = createDotIcon('dot-cyan', false);
const redIcon = createDotIcon('dot-red', true);

function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length === 0) return;
    const b = L.latLngBounds(positions);
    map.fitBounds(b, { padding: [48, 48], maxZoom: 12 });
  }, [map, positions]);
  return null;
}

function MapOverlay() {
  return (
    <div className="map-overlay" aria-hidden="true">
      <div className="map-overlay-teal" />
      <div className="map-overlay-grid" />
    </div>
  );
}

type MapMarkerEntry =
  | {
      kind: 'report';
      id: string;
      report: Report;
      lat: number;
      lng: number;
      skimmer: boolean;
      redact: boolean;
    }
  | {
      kind: 'site';
      id: string;
      lat: number;
      lng: number;
      skimmer: boolean;
      siteName: string;
      siteAddress: string;
    };

interface MapCardProps {
  sites: Site[];
  siteStatusMap: Record<string, 'healthy' | 'alert'>;
  reports?: Report[];
  viewerMasterOwnerId?: string;
}

export const MapCard: React.FC<MapCardProps> = ({
  sites,
  siteStatusMap,
  reports = [],
  viewerMasterOwnerId,
}) => {
  const markers = useMemo((): MapMarkerEntry[] => {
    const out: MapMarkerEntry[] = [];
    const seenReport = new Set<string>();
    for (const report of reports) {
      const pos = getFeedMapPosition(report, viewerMasterOwnerId, sites);
      if (!pos || seenReport.has(report.id)) continue;
      seenReport.add(report.id);
      const redact = shouldRedactScannerIdentity(report, viewerMasterOwnerId);
      out.push({
        kind: 'report',
        id: report.id,
        report,
        lat: pos.lat,
        lng: pos.lng,
        skimmer: isSkimmerReport(report),
        redact,
      });
    }
    if (out.length === 0) {
      const sitesWithCoords = sites.filter(
        (s) =>
          s.coordinates &&
          typeof (s.coordinates as any).latitude === 'number' &&
          typeof (s.coordinates as any).longitude === 'number'
      );
      sitesWithCoords.forEach((site) => {
        const lat = (site.coordinates as any).latitude;
        const lng = (site.coordinates as any).longitude;
        out.push({
          kind: 'site',
          id: site.id,
          lat,
          lng,
          skimmer: siteStatusMap[site.id] === 'alert',
          siteName: site.name ?? 'Unknown Site',
          siteAddress: site.address ?? '',
        });
      });
    }
    return out;
  }, [sites, siteStatusMap, reports, viewerMasterOwnerId]);

  const boundsPositions = useMemo(
    (): [number, number][] => markers.map((m) => [m.lat, m.lng] as [number, number]),
    [markers]
  );

  const noSkimmerCount = markers.filter((m) => !m.skimmer).length;
  const skimmerCount = markers.filter((m) => m.skimmer).length;
  const totalPoints = markers.length;

  const formatDate = (date: Report['createdAt']) => {
    const d = convertTimestamp(date);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const FeedMapMarkers: React.FC<{
    markers: MapMarkerEntry[];
    boundsPositions: [number, number][];
    viewerMasterOwnerId?: string;
  }> = ({ markers: markerList, boundsPositions: bounds, viewerMasterOwnerId: viewerId }) => (
    <>
      {bounds.length > 0 && <FitBounds positions={bounds} />}
      {markerList.map((m) => {
        if (m.kind === 'site') {
          return (
            <Marker key={m.id} position={[m.lat, m.lng]} icon={m.skimmer ? redIcon : cyanIcon}>
              <Popup>
                <Box sx={{ minWidth: '220px', maxWidth: '300px' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {m.siteName}
                  </Typography>
                  {m.siteAddress ? (
                    <Typography variant="body2" color="text.secondary">
                      {m.siteAddress}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {m.skimmer ? 'Skimmer detected' : 'No skimmers detected'}
                  </Typography>
                </Box>
              </Popup>
            </Marker>
          );
        }
        const r = m.report;
        const title = m.redact
          ? getPublicApproximateLocationLabel(r)
          : (r as any).readerDetails?.siteName?.trim() || 'Scan location';
        const addressLine = getReportLocationAddressDisplay(r, viewerId);
        return (
          <Marker key={m.id} position={[m.lat, m.lng]} icon={m.skimmer ? redIcon : cyanIcon}>
            <Popup>
              <Box sx={{ minWidth: '220px', maxWidth: '300px' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {title}
                </Typography>
                {m.redact ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Approximate area (community report)
                  </Typography>
                ) : null}
                {!m.redact && addressLine && addressLine !== '—' ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {addressLine}
                  </Typography>
                ) : m.redact ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {addressLine}
                  </Typography>
                ) : null}
                <Typography variant="body2" color={m.skimmer ? 'error.main' : 'text.secondary'} fontWeight={m.skimmer ? 600 : 400}>
                  {r.status}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatDate(r.createdAt)} · {getScannerDisplayLabel(r, viewerId)}
                </Typography>
              </Box>
            </Popup>
          </Marker>
        );
      })}
    </>
  );

  return (
    <Box className="glass-panel data-card map-card">
      <div className="map-and-stats-wrap">
        <div className="map-container">
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            className="map-leaflet"
            zoomControl={false}
            attributionControl={false}
            style={{ width: '100%', height: '100%', minHeight: 260 }}
          >
            <TileLayer url={DARK_TILES} attribution={TILE_ATTRIBUTION} />
            <FeedMapMarkers
              markers={markers}
              boundsPositions={boundsPositions}
              viewerMasterOwnerId={viewerMasterOwnerId}
            />
          </MapContainer>
          <MapOverlay />
        </div>
        <div className="map-stats-panel">
          <div className="map-stats-top">
            <div className="status-header" style={{ marginBottom: '8px' }}>
              <span className="status-label">DETECTION MAP:</span>
              <span className="text-muted">Skimmer status by location</span>
            </div>
            <span className="large-number">{totalPoints}</span>
            <div className="map-stats-legend">
              <div className="legend-item">
                <span className="dot dot-small dot-cyan" />
                <span className="text-muted text-sm">No Skimmers Detected</span>
              </div>
              <div className="legend-item">
                <span className="dot dot-small dot-red pulse-dot" />
                <span className="text-muted text-sm">Skimmers Detected</span>
              </div>
            </div>
          </div>
          <div className="map-stats-bottom">
            <div className="status-breakdown">
              <div className="text-sm" style={{ whiteSpace: 'nowrap' }}><span className="text-muted">Scan results:</span></div>
              <div className="text-sm" style={{ whiteSpace: 'nowrap' }}><strong className="text-green">{noSkimmerCount} No Skimmers Detected</strong></div>
              <div className="text-sm" style={{ whiteSpace: 'nowrap' }}><strong className="text-red">{skimmerCount} Skimmers Detected (Action required)</strong></div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};
